/* =========================================================================
   ALKEN — theme.js
   Vanilla JS only. No frameworks, no dependencies.
   -------------------------------------------------------------------------
   01. Helpers
   02. Scroll reveal
   03. Header behavior
   04. Announcement rotator
   05. Drawer / overlay manager
   06. Cart (API + drawer rendering)
   07. Quick add
   08. Product form (PDP)
   09. Gallery
   10. Sticky add-to-cart
   11. Size guide modal
   12. Delivery estimate
   13. Recently viewed
   ========================================================================= */
(function () {
  'use strict';

  var settings = window.theme || {};

  /* ---------------------------------------------------------- 01. Helpers */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) return res.json().then(function (data) { throw data; });
      return res.json();
    });
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var moneyFormat = format || settings.moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    switch (moneyFormat.match(placeholderRegex)[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return moneyFormat.replace(placeholderRegex, value);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  /* ---------------------------------------------------- 02. Scroll reveal */
  function initReveal() {
    var items = qsa('[data-reveal]');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach(function (el) { observer.observe(el); });
  }

  /* -------------------------------------------------- 03. Header behavior */
  function initHeader() {
    var wrap = qs('[data-header-wrap]');
    if (!wrap) return;
    var lastY = window.scrollY;
    var ticking = false;

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var y = window.scrollY;
        if (y > 240 && y > lastY + 6) {
          wrap.classList.add('is-hidden');
        } else if (y < lastY - 6 || y < 240) {
          wrap.classList.remove('is-hidden');
        }
        lastY = y;
        ticking = false;
      });
    }, { passive: true });
  }

  /* --------------------------------------------- 04. Announcement rotator */
  function initAnnouncement() {
    var bar = qs('[data-announcement]');
    if (!bar) return;
    var items = qsa('[data-announcement-item]', bar);
    if (items.length < 2) return;
    var index = 0;
    setInterval(function () {
      items[index].hidden = true;
      index = (index + 1) % items.length;
      items[index].hidden = false;
    }, 4500);
  }

  /* --------------------------------------- 05. Drawer / overlay manager */
  var overlay = null;
  var openPanel = null;

  function getOverlay() {
    if (!overlay) overlay = qs('[data-page-overlay]');
    return overlay;
  }

  function openDrawer(panel) {
    if (!panel) return;
    if (openPanel && openPanel !== panel) closeDrawer(openPanel);
    panel.classList.add('is-open');
    panel.removeAttribute('aria-hidden');
    getOverlay().classList.add('is-active');
    document.body.classList.add('drawer-open');
    openPanel = panel;
    var focusable = panel.querySelector('button, a, input, [tabindex]');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  function closeDrawer(panel) {
    panel = panel || openPanel;
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    getOverlay().classList.remove('is-active');
    document.body.classList.remove('drawer-open');
    openPanel = null;
  }

  function initDrawers() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-drawer-open]');
      if (opener) {
        event.preventDefault();
        openDrawer(qs('[data-drawer="' + opener.getAttribute('data-drawer-open') + '"]'));
        return;
      }
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        closeDrawer();
      }
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeDrawer();
    });
    var pageOverlay = getOverlay();
    if (pageOverlay) pageOverlay.addEventListener('click', function () { closeDrawer(); });
  }

  /* --------------------------------------------------------- 06. Cart */
  var Cart = {
    state: null,

    fetch: function () {
      return fetchJSON('/cart.js').then(function (cart) {
        Cart.state = cart;
        Cart.render(cart);
        return cart;
      });
    },

    add: function (formData) {
      return fetchJSON(settings.cartAddUrl + '.js', { method: 'POST', body: formData })
        .then(function () { return Cart.fetch(); });
    },

    addId: function (variantId, quantity) {
      var body = new FormData();
      body.append('id', variantId);
      body.append('quantity', quantity || 1);
      return Cart.add(body);
    },

    changeLine: function (line, quantity) {
      var body = new FormData();
      body.append('line', line);
      body.append('quantity', quantity);
      return fetchJSON(settings.cartChangeUrl + '.js', { method: 'POST', body: body })
        .then(function (cart) {
          Cart.state = cart;
          Cart.render(cart);
          return cart;
        });
    },

    render: function (cart) {
      qsa('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
        el.setAttribute('data-count', cart.item_count);
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });

      var linesEl = qs('[data-cart-lines]');
      var emptyEl = qs('[data-cart-empty]');
      var footerEl = qs('[data-cart-footer]');

      if (emptyEl) emptyEl.hidden = cart.item_count > 0;
      if (footerEl) footerEl.hidden = cart.item_count === 0;

      if (linesEl) {
        linesEl.hidden = cart.item_count === 0;
        linesEl.innerHTML = cart.items.map(function (item, i) {
          return Cart.lineHTML(item, i + 1);
        }).join('');
      }

      qsa('[data-cart-subtotal]').forEach(function (el) {
        el.textContent = formatMoney(cart.total_price);
      });
      qsa('[data-cart-installments]').forEach(function (el) {
        var n = parseInt(el.getAttribute('data-count') || '0', 10);
        var value = qs('strong', el);
        el.hidden = !(n > 1 && cart.total_price > 0);
        if (!el.hidden && value) {
          value.textContent = n + 'x de ' + formatMoney(Math.ceil(cart.total_price / n));
        }
      });
      qsa('[data-cart-count-inline]').forEach(function (el) {
        el.textContent = '(' + cart.item_count + ')';
      });

      Cart.renderShippingMeter(cart);
      Cart.renderUpsell(cart);
    },

    lineHTML: function (item, line) {
      var image = item.featured_image && item.featured_image.url
        ? item.featured_image.url.replace(/(\.[a-z]{3,4})(\?|$)/, '_240x$1$2')
        : '';
      var variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? '<div class="cart-line__variant">' + escapeHtml(item.variant_title) + '</div>' : '';
      var priceHtml = item.original_line_price !== item.final_line_price
        ? '<span class="price__compare">' + formatMoney(item.original_line_price) + '</span> ' + formatMoney(item.final_line_price)
        : formatMoney(item.final_line_price);

      return (
        '<div class="cart-line" data-line="' + line + '">' +
          '<a class="cart-line__media" href="' + item.url + '">' +
            (image ? '<img src="' + image + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="240" height="300">' : '') +
          '</a>' +
          '<div class="cart-line__info">' +
            '<a class="cart-line__title" href="' + item.url + '">' + escapeHtml(item.product_title) + '</a>' +
            variantTitle +
            '<div class="cart-line__bottom">' +
              '<div class="qty" data-line-qty>' +
                '<button type="button" class="qty__btn" data-qty-change="-1" aria-label="Decrease quantity">&minus;</button>' +
                '<input class="qty__value" type="number" value="' + item.quantity + '" min="0" data-qty-input aria-label="Quantity">' +
                '<button type="button" class="qty__btn" data-qty-change="1" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<span class="cart-line__price">' + priceHtml + '</span>' +
            '</div>' +
            '<button type="button" class="cart-line__remove" data-line-remove>Remover</button>' +
          '</div>' +
        '</div>'
      );
    },

    renderShippingMeter: function (cart) {
      var meter = qs('[data-shipping-meter]');
      if (!meter) return;
      var threshold = settings.freeShippingThreshold || 0;
      if (!threshold) { meter.hidden = true; return; }
      meter.hidden = false;

      var label = qs('[data-shipping-label]', meter);
      var fill = qs('[data-shipping-fill]', meter);
      var remaining = threshold - cart.total_price;
      var progress = Math.min(100, (cart.total_price / threshold) * 100);

      if (fill) fill.style.width = progress + '%';
      if (label) {
        label.innerHTML = remaining <= 0
          ? '<strong>' + settings.strings.freeShippingUnlocked + '</strong>'
          : settings.strings.freeShippingRemaining.replace('[amount]', '<strong>' + formatMoney(remaining) + '</strong>');
      }
    },

    /* "Pairs well with" — recommendations for the first cart item, excluding
       products already in the cart. Cached per anchor product. */
    renderUpsell: function (cart) {
      var box = qs('[data-cart-upsell]');
      if (!box) return;
      if (!settings.cartUpsellEnabled || cart.item_count === 0) { box.hidden = true; return; }

      var anchorId = cart.items[0].product_id;
      var inCart = cart.items.map(function (item) { return item.product_id; });

      function toCents(price) {
        if (typeof price === 'string') return Math.round(parseFloat(price) * 100);
        return price;
      }

      function imageUrl(product) {
        var src = product.featured_image;
        if (src && typeof src === 'object') src = src.url || '';
        if (!src && product.images && product.images.length) src = product.images[0];
        return src ? String(src).replace(/(\.[a-z]{3,4})(\?|$)/, '_180x$1$2') : '';
      }

      function paint(products) {
        var picks = products.filter(function (p) {
          return inCart.indexOf(p.id) === -1 && p.available !== false;
        }).slice(0, 2);
        if (!picks.length) { box.hidden = true; return; }

        qs('[data-cart-upsell-items]', box).innerHTML = picks.map(function (p) {
          var img = imageUrl(p);
          return (
            '<div class="cart-upsell__item">' +
              '<a class="cart-upsell__thumb" href="' + p.url + '">' +
                (img ? '<img src="' + img + '" alt="" loading="lazy" width="180" height="225">' : '') +
              '</a>' +
              '<div>' +
                '<a class="cart-upsell__name" href="' + p.url + '">' + escapeHtml(p.title) + '</a>' +
                '<div class="cart-upsell__price">' + formatMoney(toCents(p.price)) + '</div>' +
              '</div>' +
              '<button type="button" class="cart-upsell__add" data-quick-add="' + p.handle + '">Adicionar</button>' +
            '</div>'
          );
        }).join('');
        box.hidden = false;
      }

      if (Cart._upsellKey === anchorId && Cart._upsellCache) {
        paint(Cart._upsellCache);
        return;
      }
      fetchJSON(settings.rootUrl.replace(/\/$/, '') + '/recommendations/products.json?product_id=' + anchorId + '&limit=6')
        .then(function (data) {
          Cart._upsellCache = data.products || [];
          Cart._upsellKey = anchorId;
          paint(Cart._upsellCache);
        })
        .catch(function () { box.hidden = true; });
    }
  };

  function initCartInteractions() {
    document.addEventListener('click', function (event) {
      var lineEl = event.target.closest('.cart-line');
      if (!lineEl) return;
      var line = parseInt(lineEl.getAttribute('data-line'), 10);

      var change = event.target.closest('[data-qty-change]');
      if (change) {
        var input = qs('[data-qty-input]', lineEl);
        var next = Math.max(0, parseInt(input.value, 10) + parseInt(change.getAttribute('data-qty-change'), 10));
        Cart.changeLine(line, next);
        return;
      }
      if (event.target.closest('[data-line-remove]')) {
        Cart.changeLine(line, 0);
      }
    });

    document.addEventListener('change', function (event) {
      var input = event.target.closest('[data-qty-input]');
      if (!input) return;
      var lineEl = input.closest('.cart-line');
      if (!lineEl) return;
      Cart.changeLine(parseInt(lineEl.getAttribute('data-line'), 10), Math.max(0, parseInt(input.value, 10) || 0));
    });
  }

  /* ------------------------------------------------------ 07. Quick add */
  var QuickAdd = {
    root: null,

    open: function (handle, trigger) {
      if (trigger) trigger.classList.add('is-loading');
      var done = function () { if (trigger) trigger.classList.remove('is-loading'); };

      fetchJSON(settings.rootUrl.replace(/\/$/, '') + '/products/' + handle + '.js')
        .then(function (product) {
          // Single-variant products skip the sheet — one tap, straight to cart
          if (product.variants.length === 1 && product.variants[0].available) {
            return Cart.addId(product.variants[0].id, 1).then(function () {
              done();
              openDrawer(qs('[data-drawer="cart"]'));
            });
          }
          done();
          QuickAdd.render(product);
        })
        .catch(function () {
          done();
          window.location.href = settings.rootUrl.replace(/\/$/, '') + '/products/' + handle;
        });
    },

    render: function (product) {
      var root = qs('#QuickAddRoot');
      if (!root) return;

      var availableVariants = product.variants.filter(function (v) { return v.available; });
      var singleVariant = product.variants.length === 1;

      var optionsHtml = singleVariant ? '' : product.options.map(function (option, optionIndex) {
        var values = [];
        product.variants.forEach(function (v) {
          var value = v.options[optionIndex];
          if (values.indexOf(value) === -1) values.push(value);
        });
        return (
          '<div class="option-group">' +
            '<div class="option-group__label">' + escapeHtml(option) + '</div>' +
            '<div class="option-values" data-option-index="' + optionIndex + '">' +
              values.map(function (value) {
                return '<button type="button" class="option-pill" data-value="' + escapeHtml(value) + '">' + escapeHtml(value) + '</button>';
              }).join('') +
            '</div>' +
          '</div>'
        );
      }).join('');

      root.innerHTML =
        '<div class="quick-add drawer-panel" data-drawer="quick-add" role="dialog" aria-modal="true" aria-label="Quick add">' +
          '<div class="quick-add__head">' +
            '<div class="quick-add__thumb">' +
              (product.featured_image ? '<img src="' + product.featured_image + '" alt="" width="140" height="175">' : '') +
            '</div>' +
            '<div>' +
              '<p class="quick-add__title">' + escapeHtml(product.title) + '</p>' +
              '<span class="quick-add__price" data-quick-price>' + formatMoney(product.price) + '</span>' +
            '</div>' +
            '<button type="button" class="drawer__close" data-drawer-close aria-label="Close">' +
              '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 2l12 12M14 2L2 14"/></svg>' +
            '</button>' +
          '</div>' +
          optionsHtml +
          '<button type="button" class="btn btn--primary btn--full" data-quick-atc ' + (singleVariant ? '' : 'disabled') + '>' +
            (singleVariant && !availableVariants.length ? settings.strings.soldOut : settings.strings.addToCart) +
          '</button>' +
        '</div>';

      var panel = qs('[data-drawer="quick-add"]', root);
      var atcBtn = qs('[data-quick-atc]', panel);
      var selected = {};
      var currentVariant = singleVariant ? product.variants[0] : null;

      if (singleVariant && !product.variants[0].available) atcBtn.disabled = true;

      panel.addEventListener('click', function (event) {
        var pill = event.target.closest('.option-pill');
        if (pill) {
          var group = pill.closest('[data-option-index]');
          qsa('.option-pill', group).forEach(function (p) { p.classList.remove('is-selected'); });
          pill.classList.add('is-selected');
          selected[group.getAttribute('data-option-index')] = pill.getAttribute('data-value');

          if (Object.keys(selected).length === product.options.length) {
            currentVariant = product.variants.find(function (v) {
              return v.options.every(function (value, i) { return selected[i] === value; });
            }) || null;
            if (currentVariant) {
              qs('[data-quick-price]', panel).textContent = formatMoney(currentVariant.price);
              atcBtn.disabled = !currentVariant.available;
              atcBtn.textContent = currentVariant.available ? settings.strings.addToCart : settings.strings.soldOut;
            } else {
              atcBtn.disabled = true;
              atcBtn.textContent = settings.strings.unavailable;
            }
          }
          return;
        }

        if (event.target.closest('[data-quick-atc]') && currentVariant) {
          atcBtn.disabled = true;
          atcBtn.textContent = settings.strings.adding;
          Cart.addId(currentVariant.id, 1).then(function () {
            closeDrawer(panel);
            root.innerHTML = '';
            openDrawer(qs('[data-drawer="cart"]'));
          }).catch(function () {
            atcBtn.disabled = false;
            atcBtn.textContent = settings.strings.addToCart;
          });
        }
      });

      window.requestAnimationFrame(function () { openDrawer(panel); });
    }
  };

  function initQuickAdd() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-quick-add]');
      if (!trigger) return;
      event.preventDefault();
      QuickAdd.open(trigger.getAttribute('data-quick-add'), trigger);
    });
  }

  /* ------------------------------------------- 08. Product form (PDP) */
  function initProductForms() {
    qsa('[data-product-form-wrapper]').forEach(function (wrapper) {
      var form = qs('form[data-product-form]', wrapper);
      var dataEl = qs('[data-product-json]', wrapper);
      if (!form || !dataEl) return;

      var product = JSON.parse(dataEl.textContent);
      var variants = product.variants;
      var idInput = qs('input[name="id"]', form);
      var atcBtn = qs('[data-atc]', wrapper);
      var priceEl = qs('[data-pdp-price]', wrapper);
      var stockEl = qs('[data-stock-note]', wrapper);
      var lowStockThreshold = parseInt(wrapper.getAttribute('data-low-stock') || '0', 10);
      var selected = {};

      qsa('.option-values[data-option-index]', wrapper).forEach(function (group) {
        var index = group.getAttribute('data-option-index');
        var preselected = qs('.option-pill.is-selected', group);
        if (preselected) selected[index] = preselected.getAttribute('data-value');
      });

      function findVariant() {
        return variants.find(function (v) {
          return v.options.every(function (value, i) { return selected[i] === value; });
        }) || null;
      }

      function updateAvailabilityHints() {
        qsa('.option-values[data-option-index]', wrapper).forEach(function (group) {
          var index = parseInt(group.getAttribute('data-option-index'), 10);
          qsa('.option-pill', group).forEach(function (pill) {
            var value = pill.getAttribute('data-value');
            var anyAvailable = variants.some(function (v) {
              if (v.options[index] !== value) return false;
              return v.options.every(function (optValue, i) {
                if (i === index) return true;
                return !selected[i] || selected[i] === optValue;
              }) && v.available;
            });
            pill.classList.toggle('is-unavailable', !anyAvailable);
          });
        });
      }

      function updateUI() {
        var variant = findVariant();
        var allSelected = Object.keys(selected).length === product.options.length;

        if (!allSelected) return;

        if (!variant) {
          atcBtn.disabled = true;
          atcBtn.textContent = settings.strings.unavailable;
          if (stockEl) stockEl.hidden = true;
          return;
        }

        idInput.value = variant.id;

        if (priceEl) {
          var html = '<span class="price__current">' + formatMoney(variant.price) + '</span>';
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            html += ' <span class="price__compare">' + formatMoney(variant.compare_at_price) + '</span>';
          }
          priceEl.innerHTML = html;
        }

        var instSpan = qs('[data-pdp-installments] [data-installments]');
        if (instSpan) {
          var instCount = parseInt(instSpan.getAttribute('data-count') || '0', 10);
          var instValue = qs('strong', instSpan);
          if (instCount > 1 && instValue) {
            instValue.textContent = instCount + 'x de ' + formatMoney(Math.ceil(variant.price / instCount));
          }
        }

        atcBtn.disabled = !variant.available;
        atcBtn.textContent = variant.available ? settings.strings.addToCart : settings.strings.soldOut;

        if (stockEl) {
          var qty = variant.inventory_quantity;
          var show = variant.available &&
            variant.inventory_management &&
            lowStockThreshold > 0 &&
            typeof qty === 'number' && qty > 0 && qty <= lowStockThreshold;
          stockEl.hidden = !show;
          if (show) stockEl.textContent = settings.strings.onlyLeft.replace('[count]', qty);
        }

        var stickyPrice = qs('[data-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

        if (window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      wrapper.addEventListener('click', function (event) {
        var pill = event.target.closest('.option-pill');
        if (!pill || pill.closest('.quick-add')) return;
        var group = pill.closest('[data-option-index]');
        if (!group) return;
        qsa('.option-pill', group).forEach(function (p) { p.classList.remove('is-selected'); });
        pill.classList.add('is-selected');
        selected[group.getAttribute('data-option-index')] = pill.getAttribute('data-value');
        var selectedLabel = qs('[data-selected-value]', group.closest('.option-group'));
        if (selectedLabel) selectedLabel.textContent = pill.getAttribute('data-value');
        updateAvailabilityHints();
        updateUI();
      });

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (atcBtn.disabled) return;
        var originalText = atcBtn.textContent;
        atcBtn.disabled = true;
        atcBtn.textContent = settings.strings.adding;

        Cart.add(new FormData(form)).then(function () {
          atcBtn.textContent = settings.strings.added;
          setTimeout(function () {
            atcBtn.disabled = false;
            atcBtn.textContent = originalText;
          }, 1500);
          openDrawer(qs('[data-drawer="cart"]'));
        }).catch(function (error) {
          atcBtn.disabled = false;
          atcBtn.textContent = originalText;
          alert(error && error.description ? error.description : 'Could not add to cart.');
        });
      });

      updateAvailabilityHints();
      updateUI();
    });
  }

  /* ---------------------------------------------------- 09. Gallery */
  function initGallery() {
    var gallery = qs('[data-pdp-gallery]');
    if (!gallery) return;
    var track = qs('[data-gallery-track]', gallery);
    var slides = qsa('.pdp-gallery__slide', gallery);
    var dots = qsa('.pdp-gallery__dot', gallery);

    if (track && dots.length) {
      track.addEventListener('scroll', function () {
        window.requestAnimationFrame(function () {
          var index = Math.round(track.scrollLeft / track.offsetWidth);
          dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === index); });
        });
      }, { passive: true });
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          track.scrollTo({ left: i * track.offsetWidth, behavior: 'smooth' });
        });
      });
    }

    // Click-to-zoom on desktop
    if (window.matchMedia('(min-width: 990px)').matches) {
      slides.forEach(function (slide) {
        var img = qs('img', slide);
        if (!img) return;
        img.addEventListener('click', function (event) {
          var zoomed = img.classList.toggle('is-zoomed');
          if (zoomed) {
            var rect = slide.getBoundingClientRect();
            var x = ((event.clientX - rect.left) / rect.width) * 100;
            var y = ((event.clientY - rect.top) / rect.height) * 100;
            img.style.transformOrigin = x + '% ' + y + '%';
          }
        });
        img.addEventListener('mouseleave', function () { img.classList.remove('is-zoomed'); });
      });
    }
  }

  /* --------------------------------------- 10. Sticky add-to-cart bar */
  function initStickyAtc() {
    var bar = qs('[data-sticky-atc]');
    var anchor = qs('[data-atc]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
    }, { threshold: 0 });
    observer.observe(anchor);

    var trigger = qs('[data-sticky-trigger]', bar);
    if (trigger) {
      trigger.addEventListener('click', function () {
        var form = qs('form[data-product-form]');
        if (form) form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }
  }

  /* ----------------------------------------------- 11. Size guide modal */
  function initModals() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-modal-open]');
      if (opener) {
        event.preventDefault();
        var modal = qs('[data-modal="' + opener.getAttribute('data-modal-open') + '"]');
        if (modal) {
          modal.classList.add('is-open');
          getOverlay().classList.add('is-active');
          document.body.classList.add('drawer-open');
        }
        return;
      }
      if (event.target.closest('[data-modal-close]')) {
        closeModals();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeModals();
    });
    var pageOverlay = getOverlay();
    if (pageOverlay) pageOverlay.addEventListener('click', closeModals);

    function closeModals() {
      var open = qs('.modal.is-open');
      if (!open) return;
      open.classList.remove('is-open');
      getOverlay().classList.remove('is-active');
      document.body.classList.remove('drawer-open');
    }
  }

  /* -------------------------------------------- 12. Delivery estimate */
  function initDeliveryEstimate() {
    qsa('[data-delivery-estimate]').forEach(function (el) {
      var min = parseInt(el.getAttribute('data-min-days') || '3', 10);
      var max = parseInt(el.getAttribute('data-max-days') || '7', 10);
      var format = { day: 'numeric', month: 'short' };
      var from = new Date(); from.setDate(from.getDate() + min);
      var to = new Date(); to.setDate(to.getDate() + max);
      /* Loja BR: sempre pt-BR. O lang do documento segue o idioma da loja no
         admin (hoje "en"), o que renderizava "Aug 6 – Aug 10". */
      var locale = 'pt-BR';
      var strip = function (d) { return d.toLocaleDateString(locale, format).replace(/\.$/, ''); };
      el.textContent = strip(from) + ' – ' + strip(to);
    });
  }

  /* ------------------------------------ 12b. Consulta de frete por CEP */
  /* Dias somados ao prazo base conforme a UF de destino. Estimativa — o valor
     real do frete continua vindo do checkout. */
  var UF_EXTRA_DAYS = {
    SP: 0, RJ: 1, MG: 1, ES: 1,
    PR: 2, SC: 2, RS: 3,
    DF: 3, GO: 3, MS: 3, MT: 4,
    BA: 4, SE: 4, AL: 4, PE: 4,
    PB: 5, RN: 5, CE: 5, PI: 5, MA: 5, TO: 5,
    RO: 7, PA: 6, AP: 7, AM: 7, RR: 8, AC: 8
  };
  var CEP_KEY = 'alken:cep';

  function initShippingEstimator() {
    var root = qs('[data-shipping-estimator]');
    if (!root) return;
    var input = qs('[data-cep-input]', root);
    var btn = qs('[data-cep-submit]', root);
    var out = qs('[data-cep-result]', root);
    if (!input || !btn || !out) return;

    var minDays = parseInt(root.getAttribute('data-min-days') || '3', 10);
    var maxDays = parseInt(root.getAttribute('data-max-days') || '7', 10);
    var threshold = parseFloat((root.getAttribute('data-free-threshold') || '170').replace(',', '.'));

    function mask(v) {
      v = String(v).replace(/\D/g, '').slice(0, 8);
      return v.length > 5 ? v.slice(0, 5) + '-' + v.slice(5) : v;
    }

    function fmtDate(d) {
      return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace(/\.$/, '');
    }

    function show(html, isError) {
      out.hidden = false;
      out.innerHTML = html;
      out.classList.toggle('is-error', !!isError);
    }

    function lookup() {
      var cep = input.value.replace(/\D/g, '');
      if (cep.length !== 8) {
        show('Digite um CEP com 8 dígitos.', true);
        return;
      }
      show('Consultando…');
      fetch('https://viacep.com.br/ws/' + cep + '/json/')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (!d || d.erro) {
            show('CEP não encontrado. Confira o número.', true);
            return;
          }
          var extra = typeof UF_EXTRA_DAYS[d.uf] === 'number' ? UF_EXTRA_DAYS[d.uf] : 4;
          var from = new Date(); from.setDate(from.getDate() + minDays + extra);
          var to = new Date(); to.setDate(to.getDate() + maxDays + extra);
          var place = (d.localidade || '') + (d.uf ? '/' + d.uf : '');

          show(
            '<strong>' + place + '</strong> · chega entre ' + fmtDate(from) + ' e ' + fmtDate(to) +
            '<br><span class="ship-est__free">Frete grátis nas compras acima de ' + formatMoney(threshold * 100) + '</span>' +
            '<br><span class="ship-est__note">Prazo estimado. O valor do frete aparece no checkout.</span>'
          );

          try { localStorage.setItem(CEP_KEY, cep); } catch (e) { /* storage unavailable */ }
        })
        .catch(function () {
          show('Não consegui consultar agora. Tente novamente em instantes.', true);
        });
    }

    input.addEventListener('input', function () { input.value = mask(input.value); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); lookup(); }
    });
    btn.addEventListener('click', lookup);

    /* Se já consultou antes, repete sozinho — o cliente não digita duas vezes. */
    try {
      var saved = localStorage.getItem(CEP_KEY);
      if (saved) { input.value = mask(saved); lookup(); }
    } catch (e) { /* storage unavailable */ }
  }

  /* --------------------------------------------- 13. Recently viewed */
  var RECENT_KEY = 'alken:recently-viewed';

  function trackRecentlyViewed() {
    var el = qs('[data-product-handle]');
    if (!el) return;
    var handle = el.getAttribute('data-product-handle');
    try {
      var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      list = list.filter(function (h) { return h !== handle; });
      list.unshift(handle);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (e) { /* storage unavailable */ }
  }

  function renderRecentlyViewed() {
    var container = qs('[data-recently-viewed]');
    if (!container) return;
    var currentHandle = container.getAttribute('data-exclude') || '';
    var limit = parseInt(container.getAttribute('data-limit') || '4', 10);
    var list = [];
    try { list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) { return; }
    list = list.filter(function (h) { return h !== currentHandle; }).slice(0, limit);

    if (!list.length) {
      var section = container.closest('[data-recently-viewed-section]');
      if (section) section.hidden = true;
      return;
    }

    Promise.all(list.map(function (handle) {
      return fetchJSON(settings.rootUrl.replace(/\/$/, '') + '/products/' + handle + '.js').catch(function () { return null; });
    })).then(function (products) {
      var cards = products.filter(Boolean).map(function (product) {
        var img = product.featured_image
          ? product.featured_image.replace(/(\.[a-z]{3,4})(\?|$)/, '_720x$1$2') : '';
        return (
          '<div class="product-card" data-reveal>' +
            '<div class="product-card__media">' +
              (img ? '<img class="product-card__img product-card__img--primary" src="' + img + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="720" height="900">' : '') +
            '</div>' +
            '<div class="product-card__info">' +
              '<h3 class="product-card__title"><a href="' + product.url + '">' + escapeHtml(product.title) + '</a></h3>' +
              '<span class="price"><span class="price__current">' + formatMoney(product.price) + '</span></span>' +
            '</div>' +
          '</div>'
        );
      });
      container.innerHTML = cards.join('');
      initReveal();
    });
  }

  /* ------------------------------------------------------------ Boot */
  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    initHeader();
    initAnnouncement();
    initDrawers();
    initCartInteractions();
    initQuickAdd();
    initProductForms();
    initGallery();
    initStickyAtc();
    initModals();
    initDeliveryEstimate();
    initShippingEstimator();
    trackRecentlyViewed();
    renderRecentlyViewed();
    Cart.fetch();
  });
})();
